import React, { useContext, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'

import { makeStyles } from '@material-ui/core/styles'
import { Grid, Typography, Select, FormControl, MenuItem, ListItemIcon } from '@material-ui/core'
import { ExpandMore } from '@material-ui/icons'

import api from '~/api'
import actions from '~/actions'
import { getUrlParams } from '~/utils'

import useEcommerce from '~/components/Ecommerce/useEcommerce'
import { useLiterals } from '~/components/Literals'
import { selectChart } from '~/selectors'
import { OnlineStoreContext } from '.'

const useStyles = makeStyles((theme) => ({
  containerInput: {
    backgroundColor: '#EEEBEB',
    width: '100%',
    display: 'grid',
    placeItems: 'center',

    '& > div': {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      backgroundColor: '#EEEBEB',
      padding: 16,
      width: '100%',
      maxWidth: 700
    }
  },
  select: {
    backgroundColor: '#FFF',

    '& > .MuiSelect-root': {
      display: 'flex',
      alignItems: 'center',
      fontSize: '1rem'
    }
  },
  icon: {
    fill: theme.palette.primary.main,
    marginTop: -1,
    fontSize: 28
  },
  productImg: {
    maxWidth: '100%',
    height: 30
  },
  paper: {
    maxHeight: 300,
    ...theme.gofind.scrollBar
  }
}))

function trackApi(data, product, store, config, position) {
  api.clickTrack({
    gtin: product && product.gtin,
    productName: product && product.name,
    name: store ? store.name : 'all',
    cnpj: store ? store.cnpj : '00000000000000',
    brand: config.isOndetem ? product.brand : null,
    user: position?.user,
    ...data
  })
}

export function FormSelectProduct({ state, dispatch, config, position }) {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const dispatcher = useDispatch()

  const [chart] = useSelector(selectChart, shallowEqual)

  const { getLiteral } = useLiterals()
  const { openEcommerce, loadingEcommerce, dataEcommerce, setApiEcommerce, setOpenEcommerce } =
    useEcommerce()
  const { isEnableAlertPrice, priceMonitoring } = useContext(OnlineStoreContext)

  const { period } = chart

  // Somewhere else in your file
  function shouldRunEffect(gtin, config, { lat, lng }) {
    return gtin && gtin !== 'none' && config && lat && lng
  }

  async function getListProducts(cnpj) {
    const products = await api.getProducts(cnpj)

    const productsChangedImage = products.map((p) => {
      const changeUrl = p.imageurl
        .replace('https://images.gofind.online/', 'https://images.pertinhodemim.com/')
        .replace(
          'https://images.pertinhodemim.com/',
          'https://s3-sa-east-1.amazonaws.com/gofind.img.repo/'
        )

      return {
        ...p,
        imageurl: changeUrl.includes('?') ? `${changeUrl}&height=300` : `${changeUrl}?height=300`
      }
    })

    dispatch({ products: productsChangedImage })
  }

  async function handleRequestEcommerceProduct({ gtin }) {
    setApiEcommerce({ name: 'ecommerceGoogle', location: position?.user?.city })

    const brandName = config?.brandName
    const unusedStore = null
    const currentProduct = state.products.find((p) => p.gtin === gtin)

    dispatch({ gtin, currentProduct })
    trackApi({ event: 'ecommerce', brandName }, currentProduct, unusedStore, config, position)

    const paramsToSaveLogger = {
      lat: position.user?.lat,
      lon: position.user?.lng,
      cnpj: config.cnpj,
      gtin
    }

    if (currentProduct) {
      dispatch({ ecommerce: { open: currentProduct, load: true } })
      setOpenEcommerce(currentProduct)

      await api.saveLogger(paramsToSaveLogger)

      return
    }

    setOpenEcommerce('all')
  }

  function handleChangeProduct(event) {
    if (location.search) {
      history.replace({ search: `?gtin=${event.target.value}` })
      return
    }

    handleRequestEcommerceProduct({ gtin: event.target.value })
  }

  // Save log of type:ecommerce when accessing the screen with query string
  useEffect(() => {
    if (!location.search) return

    if (!shouldRunEffect(state.gtin, config, position.user)) return

    handleRequestEcommerceProduct({ gtin: state.gtin })
  }, [state.gtin, config, position.user?.lat, position.user?.lng])

  useEffect(() => {
    const setUrlParams = () => {
      const queryStringGtin = getUrlParams(location.search)
      dispatch({ gtin: queryStringGtin.gtin ?? 'none' })
    }

    setUrlParams()
  }, [location.search])

  useEffect(() => {
    getListProducts(config.cnpj)
  }, [])

  useEffect(() => {
    async function getEcommerceHistory() {
      if (!priceMonitoring.length) {
        state.ecommerce.load &&
          dispatch({
            ecommerce: { ...state.ecommerce, load: false }
          })
        return
      }

      const isProductHavePriceMonitored = priceMonitoring.filter(
        (pm) => pm.gtin === openEcommerce?.gtin
      ).length

      if (!isProductHavePriceMonitored) {
        dispatcher(actions.chart.data([]))
        dispatch({
          ecommerce: { ...state.ecommerce, load: false }
        })
        return
      }

      try {
        dispatcher(actions.chart.load(true))
        const insights = await api.ecommerceHistoryInsights(openEcommerce.gtin, period)

        if (Array.isArray(insights)) {
          dispatcher(actions.chart.data(insights))
          return
        }

        dispatcher(actions.chart.data([]))
      } catch (err) {
        console.log('TCL: ~ getHistoryInsights ~ err', err)
      } finally {
        dispatcher(actions.chart.load(false))
      }
    }

    getEcommerceHistory()
  }, [openEcommerce, period, isEnableAlertPrice, priceMonitoring])

  useEffect(() => {
    if (dataEcommerce)
      dispatch({
        ecommerce: { ...state.ecommerce, data: dataEcommerce }
      })
  }, [dataEcommerce])

  useEffect(() => {
    if (!loadingEcommerce)
      dispatch({
        ecommerce: { ...state.ecommerce, load: false }
      })
  }, [loadingEcommerce])

  return (
    <Grid item className={classes.containerInput}>
      <Grid>
        <Typography variant="body1" style={{ fontSize: 16, marginBottom: 8 }}>
          {getLiteral('1357')}:
        </Typography>
        <FormControl variant="outlined">
          <Select
            autoFocus
            className={classes.select}
            value={state.gtin}
            onChange={handleChangeProduct}
            IconComponent={(props) => <ExpandMore {...props} />}
            inputProps={{
              classes: {
                icon: classes.icon
              }
            }}
            MenuProps={{
              classes: {
                paper: classes.paper
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'center'
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'center'
              },
              getContentAnchorEl: null
            }}
          >
            <MenuItem value="none" disabled>
              <Typography variant="body1" style={{ fontWeight: 500 }}>
                {getLiteral('1778')}...
              </Typography>
            </MenuItem>

            {!state.products.length && (
              <MenuItem disabled>
                <Typography
                  variant="body1"
                  style={{ textAlign: 'center', width: '100%', fontSize: 18 }}
                >
                  {getLiteral('1503')}...
                </Typography>
              </MenuItem>
            )}

            {state.products.map((p) => (
              <MenuItem key={p.gtin} value={p.gtin}>
                <ListItemIcon>
                  <img
                    src={p.imageurl}
                    alt={p.name}
                    className={classes.productImg}
                    loading="lazy"
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null // prevents looping

                      currentTarget.src = `https://s3.sa-east-1.amazonaws.com/gofind.img.repo/productfinder/Grupo+417.svg`
                    }}
                  />
                </ListItemIcon>
                <Typography variant="inherit">{p.name}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  )
}
